import { MEETING_ACCESS_CODE_REJECTED } from "@calcom/features/meeting-access/constants";
import { useCopy } from "@calcom/lib/hooks/useCopy";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Button } from "@calcom/ui/components/button";
import { EmailField, Input, Label } from "@calcom/ui/components/form";
import { InfoIcon } from "@coss/ui/icons";
import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import useDigitInput from "react-digit-input";
import { createPortal } from "react-dom";

type Step = "email" | "code" | "meetings";

type Meeting = {
  startTime: Date;
  endTime: Date;
  timeZone: string | null;
  clientUrl: string | null;
};

type MutationError = { message: string; data?: { code?: string } | null };

/**
 * Пояс подписан явно: клиент мог записываться из другого города, и «10:00» без пояса
 * легко прочитать не в том времени. Пояс берётся тот, что клиент выбрал при записи.
 */
export function formatMeetingTime({
  startTime,
  endTime,
  timeZone,
  locale,
}: Meeting & { locale: string }): string {
  const format = (zone: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: zone,
    }).formatRange(startTime, endTime);

  if (timeZone) {
    try {
      return `${format(timeZone)} (${timeZone})`;
    } catch {
      // Старые записи могут хранить пояс, которого браузер не знает, — показываем в местном.
    }
  }

  const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${format(localZone)} (${localZone})`;
}

/**
 * Сделан без Radix Dialog по той же причине, что и `EmailConfirmAndVerifyDialog`:
 * оверлей Radix оставался висеть поверх страницы записи и глотал клики.
 */
export function FindMyMeetingDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t, i18n } = useLocale();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  const describeError = useCallback(
    (mutationError: MutationError) => {
      if (mutationError.data?.code === "TOO_MANY_REQUESTS") return t("find_my_meeting_too_many_requests");
      if (mutationError.message === MEETING_ACCESS_CODE_REJECTED) return t(MEETING_ACCESS_CODE_REJECTED);
      return t("something_went_wrong");
    },
    [t]
  );

  const sendCodeMutation = trpc.viewer.public.sendMeetingAccessCode.useMutation({
    onSuccess: () => {
      setCode("");
      setStep("code");
    },
    onError: (mutationError) => setError(describeError(mutationError)),
  });

  const findMeetingsMutation = trpc.viewer.public.findUpcomingMeetingsByEmail.useMutation({
    onSuccess: (foundMeetings) => {
      setMeetings(foundMeetings);
      setStep("meetings");
    },
    onError: (mutationError) => setError(describeError(mutationError)),
  });

  const digits = useDigitInput({
    acceptedCharacters: /^[0-9]$/,
    length: 6,
    value: code,
    onChange: useCallback((newValue: string) => {
      setError("");
      setCode(newValue);
    }, []),
  });

  const sendCode = () => {
    setError("");
    sendCodeMutation.mutate({ email, language: i18n.language || "en" });
  };

  // Только по кнопке, без автоотправки по последней цифре: каждая отправка списывает одну
  // из трёх попыток, и опечатка при наборе не должна сжигать код сама.
  const submitCode = () => {
    setError("");
    findMeetingsMutation.mutate({ email, code });
  };

  const isCodeComplete = /^\d{6}$/.test(code.trim());

  // biome-ignore lint/correctness/useExhaustiveDependencies: сбрасываем только при закрытии, а мутации меняют ссылку на каждый рендер
  useEffect(() => {
    if (isOpen) return;
    setStep("email");
    setCode("");
    setError("");
    setMeetings([]);
    sendCodeMutation.reset();
    findMeetingsMutation.reset();
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") return null;

  const onEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendCode();
  };

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="find-my-meeting-dialog">
      <div className="fadeIn bg-neutral-800/70 dark:bg-neutral-800/80 absolute inset-0" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="find-my-meeting-title"
        className="bg-default fadeIn relative z-[101] w-full max-w-md rounded-2xl px-8 pt-8 text-left shadow-xl">
        <h2 id="find-my-meeting-title" className="text-semibold text-emphasis font-cal mb-1 text-xl">
          {step === "meetings" ? t("find_my_meeting_list_title") : t("find_my_meeting_title")}
        </h2>

        {step === "email" && (
          <form key="email" onSubmit={onEmailSubmit}>
            <p className="text-subtle mb-4 text-sm">{t("find_my_meeting_email_description")}</p>
            <EmailField
              name="email"
              label={t("find_my_meeting_email_label")}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
              data-testid="find-my-meeting-email"
            />
            <ErrorMessage error={error} />
            <DialogFooter>
              <Button type="button" color="minimal" onClick={onClose}>
                {t("close")}
              </Button>
              <Button
                type="submit"
                loading={sendCodeMutation.isPending}
                data-testid="find-my-meeting-get-code">
                {t("find_my_meeting_get_code")}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === "code" && (
          <div key="code">
            <p className="text-subtle text-sm">{t("find_my_meeting_code_description")}</p>
            <p className="text-subtle mb-4 mt-2 text-sm">{t("find_my_meeting_code_spam_hint")}</p>
            <Label htmlFor="find-my-meeting-code">{t("code")}</Label>
            <div className="flex flex-row justify-between">
              {digits.map((digit, index) => (
                <Input
                  // biome-ignore lint/suspicious/noArrayIndexKey: the six digit inputs are a fixed, never-reordered list
                  key={index}
                  id={index === 0 ? "find-my-meeting-code" : undefined}
                  className="h-12 w-12 text-xl! text-center"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  {...digit}
                  autoFocus={index === 0}
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                />
              ))}
            </div>
            <ErrorMessage error={error} />
            <div className="mt-4 text-sm">
              <button
                type="button"
                className="text-emphasis underline"
                onClick={() => {
                  setError("");
                  setStep("email");
                }}>
                {t("find_my_meeting_change_email")}
              </button>
            </div>
            <DialogFooter>
              <Button type="button" color="minimal" onClick={onClose}>
                {t("close")}
              </Button>
              <Button
                type="button"
                onClick={submitCode}
                disabled={!isCodeComplete}
                data-testid="find-my-meeting-submit-code"
                loading={findMeetingsMutation.isPending}>
                {t("find_my_meeting_get_link")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "meetings" && (
          <div key="meetings">
            {meetings.length === 0 ? (
              <p className="text-subtle mt-4 text-sm">{t("find_my_meeting_no_meetings")}</p>
            ) : (
              <ul className="mt-4 flex flex-col gap-3" data-testid="find-my-meeting-list">
                {meetings.map((meeting) => (
                  <li
                    key={meeting.startTime.toISOString()}
                    className="border-subtle flex flex-col gap-2 rounded-xl border p-4">
                    <p className="text-emphasis text-sm font-medium">
                      {formatMeetingTime({ ...meeting, locale: i18n.language || "en" })}
                    </p>
                    {meeting.clientUrl ? (
                      <MeetingLinkActions url={meeting.clientUrl} />
                    ) : (
                      <p className="text-subtle text-sm">{t("find_my_meeting_link_not_ready")}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" color="minimal" onClick={onClose}>
                {t("close")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function MeetingLinkActions({ url }: { url: string }) {
  const { t } = useLocale();
  const { isCopied, copyToClipboard } = useCopy();
  const [isCopyFailed, setIsCopyFailed] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          StartIcon="video"
          className="flex-1 justify-center">
          {t("find_my_meeting_open_link")}
        </Button>
        <Button
          type="button"
          color="secondary"
          StartIcon={isCopied ? "check" : "copy"}
          className="flex-1 justify-center"
          onClick={() => copyToClipboard(url, { onFailure: () => setIsCopyFailed(true) })}
          data-testid="find-my-meeting-copy-link">
          {isCopied ? t("copied") : t("find_my_meeting_copy_link")}
        </Button>
      </div>
      {/* Буфер обмена недоступен вне защищённого контекста (например, по http не с localhost):
          тогда отдаём ссылку текстом, чтобы её можно было выделить и скопировать вручную. */}
      {isCopyFailed && (
        <Input
          readOnly
          value={url}
          onFocus={(event) => event.target.select()}
          aria-label={t("find_my_meeting_copy_link")}
        />
      )}
    </div>
  );
}

function ErrorMessage({ error }: { error: string }) {
  if (!error) return null;

  return (
    <div className="mt-2 flex items-center gap-x-2 text-sm text-red-700" role="alert">
      <InfoIcon className="h-3 w-3 shrink-0" />
      <p>{error}</p>
    </div>
  );
}

function DialogFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-cal-muted border-muted -mx-8 mt-8 flex justify-end gap-x-2 rounded-b-2xl border px-8 py-4">
      {children}
    </div>
  );
}
